/**
 * Persists the local ACP agent's session id and config options in the CLI SQLite DB,
 * keyed by a stable slug of the BuildAutomaton cloud session id.
 */

import { withCliSqliteSync } from '../../sqlite/cli-database.js';

export interface LocalAgentSessionFileV1 {
  v: 1;
  acpSessionId: string | null;
  backendAgentType: string | null;
  configOptions: unknown[] | null;
  updatedAt: string;
}

function sessionKeyForCloudSessionId(cloudSessionId: string): string {
  const t = cloudSessionId.replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 220);
  return t.length > 0 ? t : 'session';
}

export function readLocalAgentSessionFile(cloudSessionId: string): LocalAgentSessionFileV1 | null {
  try {
    return withCliSqliteSync((db) => {
      const key = sessionKeyForCloudSessionId(cloudSessionId);
      const row = db.get(
        'SELECT acp_session_id, backend_agent_type, config_options_json, updated_at FROM agent_session WHERE session_key = ?',
        [key],
      ) as {
        acp_session_id: string | null;
        backend_agent_type: string | null;
        config_options_json: string | null;
        updated_at: string;
      } | null;
      if (!row) return null;
      let configOptions: unknown[] | null = null;
      if (row.config_options_json != null && row.config_options_json !== '') {
        try {
          const parsed = JSON.parse(row.config_options_json) as unknown;
          configOptions = Array.isArray(parsed) ? parsed : null;
        } catch {
          configOptions = null;
        }
      }
      return {
        v: 1,
        acpSessionId: row.acp_session_id,
        backendAgentType: row.backend_agent_type,
        configOptions,
        updatedAt: row.updated_at,
      };
    });
  } catch {
    return null;
  }
}

export function writeLocalAgentSessionFile(
  cloudSessionId: string,
  patch: Partial<Pick<LocalAgentSessionFileV1, 'acpSessionId' | 'backendAgentType' | 'configOptions'>>,
): void {
  try {
    withCliSqliteSync((db) => {
      const key = sessionKeyForCloudSessionId(cloudSessionId);
      const prevRow = db.get(
        'SELECT acp_session_id, backend_agent_type, config_options_json, updated_at FROM agent_session WHERE session_key = ?',
        [key],
      ) as {
        acp_session_id: string | null;
        backend_agent_type: string | null;
        config_options_json: string | null;
        updated_at: string;
      } | null;
      let prev: LocalAgentSessionFileV1 | null = null;
      if (prevRow) {
        let configOptions: unknown[] | null = null;
        if (prevRow.config_options_json != null && prevRow.config_options_json !== '') {
          try {
            const parsed = JSON.parse(prevRow.config_options_json) as unknown;
            configOptions = Array.isArray(parsed) ? parsed : null;
          } catch {
            configOptions = null;
          }
        }
        prev = {
          v: 1,
          acpSessionId: prevRow.acp_session_id,
          backendAgentType: prevRow.backend_agent_type,
          configOptions,
          updatedAt: prevRow.updated_at,
        };
      }
      const next: LocalAgentSessionFileV1 = {
        v: 1,
        acpSessionId: patch.acpSessionId !== undefined ? patch.acpSessionId : (prev?.acpSessionId ?? null),
        backendAgentType:
          patch.backendAgentType !== undefined ? patch.backendAgentType : (prev?.backendAgentType ?? null),
        configOptions: patch.configOptions !== undefined ? patch.configOptions : (prev?.configOptions ?? null),
        updatedAt: new Date().toISOString(),
      };
      const configJson = next.configOptions != null ? JSON.stringify(next.configOptions) : null;
      db.run(
        `INSERT INTO agent_session (session_key, acp_session_id, backend_agent_type, config_options_json, updated_at)
         VALUES (?, ?, ?, ?, ?)
         ON CONFLICT(session_key) DO UPDATE SET
           acp_session_id = excluded.acp_session_id,
           backend_agent_type = excluded.backend_agent_type,
           config_options_json = excluded.config_options_json,
           updated_at = excluded.updated_at`,
        [key, next.acpSessionId, next.backendAgentType, configJson, next.updatedAt],
      );
    });
  } catch {
    /* ignore disk errors — session still works without persistence */
  }
}
